import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import React, { useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, SubmitHandler, useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import InfoSection from '@/components/ui/sections/InfoSection'
import SpecialitiesAutocompleteController from '@/containers/react-hook-form/SpecialitiesAutocompleController'
import { getMinutesFromTimeString } from '@/utils/helpers'
import { StaffType } from '@/utils/onboarding/type'
import NumberInputController from '@/components/ui/inputs/NumberInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import TimePickerInputController from '@/components/ui/inputs/TimePickerInputController'

import { useSearchFormTemplatesQuery } from '@/core/services/form'
import { READINESS_CHECKLIST_CATEGORY } from '@/core/models/form.model'

import DoctorSelectionTable, { DoctorData } from './DoctorSelectionTable'
import EstablishmentSelectionTable, { EstablishmentData } from './EstablishmentSelectionTable'
import { useWaitingRoomForm, WaitingRoomFormData } from '../model/useWaitingRoomForm'

dayjs.extend(duration)

export type WaitingRoomFormHandle = {
  resetForm: () => void
}

export type WaitingRoomFormProps = {
  doctors?: DoctorData[]
  establishments?: EstablishmentData[]
  editable?: boolean
  initialValues: WaitingRoomFormData
  isAdmin?: boolean
  isSubmitting?: boolean
  onSubmit: SubmitHandler<WaitingRoomFormData>
}

const openingHourPeriodGenerator = (open: string = '09:00', close: string = '17:00') => ({
  open,
  close,
})

export const WaitingRoomForm = React.forwardRef(function WaitingRoomForm(props: WaitingRoomFormProps, ref: React.Ref<WaitingRoomFormHandle>) {
  const { doctors, establishments, editable = true, initialValues, isAdmin = false, isSubmitting = false, onSubmit } = props
  const { t } = useTranslation()

  const { rhForm } = useWaitingRoomForm({ doctors, isAdmin, initialValues, editable })

  useImperativeHandle(ref, () => ({
    resetForm: () => {
      rhForm.reset()
    },
  }))

  return (
    <FormProvider {...rhForm}>
      <form onSubmit={rhForm.handleSubmit(onSubmit, console.log)}>
        <Grid container spacing={4}>
          {/* ── Essentials: identity, hours, and (admin) who staffs the room ── */}
          <SectionGroupHeader title={t('page.createWaitingRoom.createWaitingRoomForm.group.essentials.label', { defaultValue: 'Essentiels' })} />
          <Grid item xs={12} md={6}>
            <GeneralInformationSection editable={editable} isAdmin={isAdmin} />
          </Grid>
          <Grid item xs={12} md={6} id="hours">
            <OpeningHoursSection editable={editable} />
          </Grid>
          {isAdmin && (
            <Grid item xs={12}>
              <DoctorSelectionSection doctors={doctors} editable={editable} />
            </Grid>
          )}
          {isAdmin && (
            <Grid item xs={12}>
              <EstablishmentSelectionSection establishments={establishments} editable={editable} />
            </Grid>
          )}

          {/* ── Patient flow (advanced, opt-in): how patients are distributed + prepared ── */}
          <SectionGroupHeader
            title={t('page.createWaitingRoom.createWaitingRoomForm.group.patientFlow.label', { defaultValue: 'Flux patient' })}
            subtitle={t('page.createWaitingRoom.createWaitingRoomForm.group.patientFlow.description', {
              defaultValue: 'Optionnel — répartition des patients entre les médecins et préparation avant l’arrivée du médecin.',
            })}
          />
          <Grid item xs={12} md={4}>
            <FairDistributionSection editable={editable} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ReadinessConfigSection editable={editable} />
          </Grid>
          <Grid item xs={12} md={4}>
            <CoPresenceSection editable={editable} />
          </Grid>

          {/* ── Payment ── */}
          <SectionGroupHeader title={t('page.createWaitingRoom.createWaitingRoomForm.group.payment.label', { defaultValue: 'Paiement' })} />
          <Grid item xs={12} md={6}>
            <PaymentSection editable={editable} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PaymentDialogConfigSection editable={editable} />
          </Grid>

          {editable && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <LoadingButton loading={isSubmitting} type="submit" variant="contained" data-testid="waiting-room-submit">
                  {t('common:button.submit.label')}
                </LoadingButton>
                <Button onClick={() => rhForm.reset()} type="button" variant="neutral">
                  {t('common:button.reset.label')}
                </Button>
              </Stack>
            </Grid>
          )}
        </Grid>
      </form>
    </FormProvider>
  )
})

/** Full-width group separator: an overline title + optional one-line subtitle + a divider. */
function SectionGroupHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Grid item xs={12}>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, color: 'primary.main' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
        <Divider />
      </Stack>
    </Grid>
  )
}

/** Small muted hint shown under a field/toggle to explain what it does. */
function FieldHint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
      {children}
    </Typography>
  )
}

function DoctorSelectionSection({ doctors = [], editable = true }: { doctors?: DoctorData[]; editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  const selectedDoctors = useWatch({ control: rhForm.control, name: 'doctors' })
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.doctorSelection.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.doctorSelection.description', {
        defaultValue: 'Médecins autorisés à prendre les patients de cette salle.',
      })}
    >
      <Box mt={2}>
        <DoctorSelectionTable
          data={doctors}
          onSelect={(selectedDoctorIds: string[]) => rhForm.setValue('doctors', selectedDoctorIds)}
          readOnly={!editable}
          selected={selectedDoctors}
        />
      </Box>
    </InfoSection>
  )
}

function EstablishmentSelectionSection({ establishments = [], editable = true }: { establishments?: EstablishmentData[]; editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  const selectedEstablishments = useWatch({ control: rhForm.control, name: 'establishments' })
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.establishmentSelection.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.establishmentSelection.description', {
        defaultValue: 'Établissements rattachés à cette salle d’attente.',
      })}
    >
      <Box mt={2}>
        <EstablishmentSelectionTable
          data={establishments}
          onSelect={(selectedEstablishmentIds: string[]) => rhForm.setValue('establishments', selectedEstablishmentIds)}
          readOnly={!editable}
          selected={selectedEstablishments}
        />
      </Box>
    </InfoSection>
  )
}

function GeneralInformationSection({ editable = true, isAdmin }: { editable?: boolean; isAdmin?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.generalInformation.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.generalInformation.description', {
        defaultValue: 'Nom, spécialité et temps d’attente moyen affiché aux patients.',
      })}
    >
      <Grid container rowSpacing={3} sx={{ mt: '0' }}>
        <Grid item xs={12}>
          <TextInputController control={rhForm.control} id="name" readOnly={!editable} label={t('field.name.label')} fullWidth name="name" />
        </Grid>
        <Grid item xs={12}>
          <TextInputController
            control={rhForm.control}
            id="description"
            readOnly={!editable}
            label={t('field.description.label')}
            fullWidth
            multiline
            name="description"
          />
        </Grid>
        <Grid item xs={12} id="meetings">
          <SpecialitiesAutocompleteController
            control={rhForm.control}
            id="speciality"
            name="specialities"
            editable={true}
            label={t('field.speciality.label', { context: 'plural' })}
          />
        </Grid>
        <Grid item xs={12}>
          <NumberInputController control={rhForm.control} readOnly={!editable} label={t('field.avgWaitingTime.label')} name="avgWaitingTime" />
        </Grid>
        <Grid item xs={12}>
          <CheckboxInputController
            control={rhForm.control}
            readOnly={!editable}
            label={t('field.allowPatientSelection.label')}
            name="allowPatientSelection"
          />
          <FieldHint>
            {t('field.allowPatientSelection.help', {
              defaultValue: 'Les patients choisissent un médecin précis plutôt que de suivre l’ordre de la file.',
            })}
          </FieldHint>
        </Grid>
        {isAdmin && (
          <Grid item xs={12}>
            <CheckboxInputController control={rhForm.control} readOnly={!editable} label={t('field.priority.label')} name="isPrioritized" />
            <FieldHint>{t('field.priority.help', { defaultValue: 'Met cette salle en avant dans les listes de salles d’attente.' })}</FieldHint>
          </Grid>
        )}
      </Grid>
    </InfoSection>
  )
}

function PaymentDialogConfigSection({ editable }: { editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.paymentDialogConfig.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.paymentDialogConfig.description', {
        defaultValue: 'Affiche une demande de paiement en consultation, et pour quels rôles.',
      })}
    >
      <Grid container rowSpacing={3} sx={{ mt: '0' }}>
        <Grid item xs={12}>
          <CheckboxInputController
            control={rhForm.control}
            readOnly={!editable}
            label={t('field.paymentDialogEnabled.label')}
            name="paymentDialogConfig.enabled"
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('field.paymentDialogShowForRoles.label')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <CheckboxInput
              checked={rhForm.watch('paymentDialogConfig.showForRoles').includes(StaffType.MEDECIN)}
              label={t('page.liveConsultation.invitationDialog.tab.doctor')}
              readOnly={!editable}
              onChange={(e) => {
                const current = rhForm.getValues('paymentDialogConfig.showForRoles')
                rhForm.setValue(
                  'paymentDialogConfig.showForRoles',
                  e.target.checked ? [...current, StaffType.MEDECIN] : current.filter((r) => r !== StaffType.MEDECIN)
                )
              }}
            />
            <CheckboxInput
              checked={rhForm.watch('paymentDialogConfig.showForRoles').includes(StaffType.PDS)}
              label={t('page.liveConsultation.invitationDialog.tab.nurse')}
              readOnly={!editable}
              onChange={(e) => {
                const current = rhForm.getValues('paymentDialogConfig.showForRoles')
                rhForm.setValue(
                  'paymentDialogConfig.showForRoles',
                  e.target.checked ? [...current, StaffType.PDS] : current.filter((r) => r !== StaffType.PDS)
                )
              }}
            />
          </Stack>
        </Grid>
      </Grid>
    </InfoSection>
  )
}

function FairDistributionSection({ editable }: { editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  const enabled = useWatch({ control: rhForm.control, name: 'fairDistribution.enabled' })
  const strategy = useWatch({ control: rhForm.control, name: 'fairDistribution.strategy' })
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.fairDistribution.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.fairDistribution.description', {
        defaultValue: 'Comment les patients en file sont répartis entre les médecins. Désactivé = premier arrivé, premier servi.',
      })}
    >
      <Grid container rowSpacing={3} sx={{ mt: '0' }}>
        <Grid item xs={12}>
          <CheckboxInputController
            control={rhForm.control}
            readOnly={!editable}
            label={t('field.fairDistributionEnabled.label')}
            name="fairDistribution.enabled"
          />
        </Grid>
        {enabled && (
          <>
            <Grid item xs={12}>
              <SelectInputController
                control={rhForm.control}
                readOnly={!editable}
                fullWidth
                label={t('field.fairDistributionStrategy.label')}
                name="fairDistribution.strategy"
                options={[
                  { value: 'cap', label: t('field.fairDistributionStrategy.cap.label') },
                  { value: 'offer', label: t('field.fairDistributionStrategy.offer.label') },
                ]}
              />
              <FieldHint>
                {strategy === 'offer'
                  ? t('field.fairDistributionStrategy.offer.help', {
                      defaultValue: 'Le patient en tête est réservé à un médecin à la fois, à tour de rôle ; s’il n’accepte pas, il passe au suivant.',
                    })
                  : t('field.fairDistributionStrategy.cap.help', {
                      defaultValue: 'Les médecins choisissent librement, mais chacun est limité à N patients et patiente entre deux consultations.',
                    })}
              </FieldHint>
            </Grid>
            <Grid item xs={12}>
              <NumberInputController
                control={rhForm.control}
                readOnly={!editable}
                label={t('field.fairDistributionMaxConcurrent.label')}
                name="fairDistribution.maxConcurrent"
                helperText={t('field.fairDistributionMaxConcurrent.help', { defaultValue: 'Nombre maximum de patients qu’un médecin peut avoir en même temps.' })}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberInputController
                control={rhForm.control}
                readOnly={!editable}
                label={t('field.fairDistributionCooldownSeconds.label')}
                name="fairDistribution.cooldownSeconds"
                helperText={t('field.fairDistributionCooldownSeconds.help', {
                  defaultValue: 'Secondes d’attente après une consultation avant d’en prendre une autre (laisse un tour aux collègues).',
                })}
              />
            </Grid>
            {strategy === 'offer' && (
              <>
                <Grid item xs={12}>
                  <NumberInputController
                    control={rhForm.control}
                    readOnly={!editable}
                    label={t('field.fairDistributionOfferTtl.label')}
                    name="fairDistribution.offerTtlSeconds"
                    helperText={t('field.fairDistributionOfferTtl.help', {
                      defaultValue: 'Secondes pour accepter le patient réservé avant qu’il passe au médecin suivant.',
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CheckboxInputController
                    control={rhForm.control}
                    readOnly={!editable}
                    label={t('field.fairDistributionProminentOffer.label')}
                    name="fairDistribution.prominentOffer"
                  />
                  <FieldHint>
                    {t('field.fairDistributionProminentOffer.help', {
                      defaultValue: 'Affiche une bannière pleine largeur au médecin sollicité au lieu d’une petite pastille dans la file.',
                    })}
                  </FieldHint>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </InfoSection>
  )
}

function ReadinessConfigSection({ editable }: { editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  const enabled = useWatch({ control: rhForm.control, name: 'readinessConfig.enabled' })
  // Preparation checklists are FormTemplates authored under the 'readiness-checklist' category
  const { data: checklistTemplates } = useSearchFormTemplatesQuery(
    { search: { categories: [READINESS_CHECKLIST_CATEGORY], isActive: true }, limit: 100, skip: 0, order: 'ASC' },
    { skip: !enabled }
  )
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.readinessConfig.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.readinessConfig.description', {
        defaultValue: 'L’opérateur prépare le patient puis signale au médecin qu’il peut rejoindre.',
      })}
    >
      <Grid container rowSpacing={3} sx={{ mt: '0' }}>
        <Grid item xs={12}>
          <CheckboxInputController
            control={rhForm.control}
            readOnly={!editable}
            id="readiness-enabled-checkbox"
            label={t('field.readinessEnabled.label')}
            name="readinessConfig.enabled"
          />
        </Grid>
        {enabled && (
          <>
            <Grid item xs={12}>
              <CheckboxInputController
                control={rhForm.control}
                readOnly={!editable}
                label={t('field.readinessPrioritizeReady.label')}
                name="readinessConfig.prioritizeReady"
              />
              <FieldHint>
                {t('field.readinessPrioritizeReady.help', { defaultValue: 'Remonte les patients préparés en haut de la file des médecins.' })}
              </FieldHint>
            </Grid>
            <Grid item xs={12}>
              <SelectInputController
                control={rhForm.control}
                readOnly={!editable}
                fullWidth
                id="readiness-checklist-template-select"
                label={t('field.readinessChecklistTemplate.label')}
                name="readinessConfig.checklistTemplateId"
                options={[
                  { value: '', label: t('field.readinessChecklistTemplate.none.label') },
                  ...(checklistTemplates?.items ?? []).map((template) => ({ value: template._id, label: template.title })),
                ]}
              />
              <FieldHint>
                {t('field.readinessChecklistTemplate.help', {
                  defaultValue: 'Formulaire que l’opérateur remplit pendant la préparation ; les médecins en voient l’avancement. Gérez les modèles dans « Modèles de formulaire ».',
                })}
              </FieldHint>
            </Grid>
          </>
        )}
      </Grid>
    </InfoSection>
  )
}

function CoPresenceSection({ editable }: { editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  const enabled = useWatch({ control: rhForm.control, name: 'coPresenceConfig.enabled' })

  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.coPresenceConfig.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.coPresenceConfig.description', {
        defaultValue:
          'Les médecins voient les consultations en cours. Ils peuvent toujours rouvrir les leurs ; cette option autorise aussi un collègue à rejoindre une consultation en cours.',
      })}
    >
      <Grid container rowSpacing={3} sx={{ mt: '0' }}>
        <Grid item xs={12}>
          <CheckboxInputController
            control={rhForm.control}
            readOnly={!editable}
            label={t('field.coPresenceEnabled.label')}
            name="coPresenceConfig.enabled"
          />
          <FieldHint>
            {t('field.coPresenceEnabled.help', {
              defaultValue:
                'Désactivé : seul le médecin responsable peut rouvrir sa consultation. Activé : les autres médecins de la salle voient un bouton Rejoindre.',
            })}
          </FieldHint>
        </Grid>
        {enabled && (
          <Grid item xs={12}>
            <CheckboxInputController
              control={rhForm.control}
              readOnly={!editable}
              label={t('field.coPresenceAllowAssign.label')}
              name="coPresenceConfig.allowAssign"
            />
            <FieldHint>
              {t('field.coPresenceAllowAssign.help', {
                defaultValue:
                  'Ajoute dans la fenêtre de confirmation l’option de prendre la responsabilité de la consultation. L’ancien médecin reste listé comme participant.',
              })}
            </FieldHint>
          </Grid>
        )}
      </Grid>
    </InfoSection>
  )
}

function OpeningHourField({ editable, index }: { editable?: boolean; index: number }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  const {
    formState: { errors, touchedFields },
  } = rhForm
  const { fields: periods, append, remove } = useFieldArray({ control: rhForm.control, name: `openingHours.${index}.periods` })
  return (
    <Stack direction="row" alignItems={periods.length > 0 ? 'flex-start' : 'center'} spacing={1}>
      <Box sx={{ flex: 1 }}>
        {editable ? (
          <CheckboxInput
            checked={!!periods.length}
            label={_.trim(dayjs().day(index).format('ddd'), '.').toUpperCase()}
            onChange={(event) => {
              if (event.target.checked) {
                append(openingHourPeriodGenerator())
              } else {
                rhForm.setValue(`openingHours.${index}.periods`, [])
              }
            }}
          />
        ) : (
          <Typography sx={{ lineHeight: '37px' }}>{_.trim(dayjs().day(index).format('ddd'), '.').toUpperCase()}</Typography>
        )}
      </Box>
      <Stack flex={4} spacing={1}>
        {periods.length > 0 ? (
          <>
            {_.map(periods, (_period, periodIndex) => {
              const openError = errors.openingHours?.[index]?.periods?.[periodIndex]?.open
              const closeError = errors.openingHours?.[index]?.periods?.[periodIndex]?.close
              const openTouched = touchedFields.openingHours?.[index]?.periods?.[periodIndex]?.open
              const closeTouched = touchedFields.openingHours?.[index]?.periods?.[periodIndex]?.close
              return (
                <Stack key={`day-${index}-period-${periodIndex}`} spacing={1} sx={{ maxWidth: '300px' }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                    <TimePickerInputController
                      control={rhForm.control}
                      readOnly={!editable}
                      disableHelperText
                      fullWidth
                      name={`openingHours.${index}.periods.${periodIndex}.open`}
                    />
                    <TimePickerInputController
                      control={rhForm.control}
                      readOnly={!editable}
                      disableHelperText
                      fullWidth
                      name={`openingHours.${index}.periods.${periodIndex}.close`}
                    />
                    {editable && (
                      <Tooltip title={t('common:button.delete.tooltip')}>
                        <IconButton onClick={() => remove(periodIndex)}>
                          <DeleteRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  {((openTouched && !!openError) || (closeTouched && !!closeError)) && (
                    <FormHelperText error sx={{ position: 'unset' }}>
                      <ErrorMessage error={openError?.message || closeError} />
                    </FormHelperText>
                  )}
                </Stack>
              )
            })}
          </>
        ) : (
          <Typography sx={{ lineHeight: '37px' }}>{t('page.createWaitingRoom.createWaitingRoomForm.infoSection.openingHours.message.noPeriod')}</Typography>
        )}
      </Stack>
      {editable && (
        <Tooltip title={t('common:button.add.tooltip')}>
          <IconButton
            onClick={() => {
              const lastPeriod = _.last(periods)
              if (lastPeriod) {
                const lastPeriodCloseInMinutes = getMinutesFromTimeString(lastPeriod.close)
                const nextOpen =
                  dayjs.duration(lastPeriodCloseInMinutes, 'minutes').add(1, 'h').asMinutes() < 24 * 60
                    ? dayjs.duration(lastPeriodCloseInMinutes, 'minutes').add(1, 'h').format('HH:mm')
                    : dayjs.duration(lastPeriodCloseInMinutes, 'minutes').subtract(23, 'h').format('HH:mm')
                const nextOpenInMinutes = getMinutesFromTimeString(nextOpen)
                const nextClose =
                  dayjs.duration(nextOpenInMinutes, 'minutes').add(1, 'h').asMinutes() < 24 * 60
                    ? dayjs.duration(nextOpenInMinutes, 'minutes').add(1, 'h').format('HH:mm')
                    : dayjs.duration(nextOpenInMinutes, 'minutes').subtract(23, 'h').format('HH:mm')
                append(openingHourPeriodGenerator(nextOpen, nextClose))
              } else {
                append(openingHourPeriodGenerator())
              }
            }}
          >
            <AddRoundedIcon />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  )
}

function OpeningHoursSection({ editable }: { editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  const openingHours = useWatch({ control: rhForm.control, name: 'openingHours' })
  const setOpenAllTime = () => {
    // Target each day's periods array by name: each row owns a useFieldArray on
    // `openingHours.<day>.periods`, and setting the parent `openingHours` does not re-sync them.
    _.range(7).forEach((day) => {
      rhForm.setValue(`openingHours.${day}.periods`, [openingHourPeriodGenerator('00:00', '23:59')], { shouldDirty: true, shouldValidate: true })
    })
  }
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.openingHours.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.openingHours.description', {
        defaultValue: 'Plages horaires pendant lesquelles la salle accepte des patients.',
      })}
      action={
        editable && (
          <Button onClick={setOpenAllTime} size="small" type="button" variant="neutral" data-testid="open-all-time">
            {t('page.createWaitingRoom.createWaitingRoomForm.infoSection.openingHours.openAllTime.label')}
          </Button>
        )
      }
    >
      <Stack spacing={2} mt={2} divider={<Divider />}>
        {openingHours.map((openingHour) => (
          <OpeningHourField key={openingHour.day} editable={editable} index={openingHour.day} />
        ))}
      </Stack>
    </InfoSection>
  )
}

function PaymentSection({ editable }: { editable?: boolean }) {
  const { t } = useTranslation()
  const rhForm = useFormContext<WaitingRoomFormData>()
  return (
    <InfoSection
      label={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.payment.label')}
      description={t('page.createWaitingRoom.createWaitingRoomForm.infoSection.payment.summary', {
        defaultValue: 'Tarif de la consultation dans cette salle (0 = gratuit).',
      })}
    >
      <Grid container rowSpacing={3} sx={{ mt: '0' }}>
        <Grid item xs={12}>
          <NumberInputController control={rhForm.control} readOnly={!editable} label={t('field.price.label')} name="price" placeholder="0 €" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('page.createWaitingRoom.createWaitingRoomForm.infoSection.payment.description')}
          </Typography>
          {/* <AutocompleteField
            readOnly={!editable}
            label={t('field.paymentReceiverId.label')}
            name="paymentReceiverId"
            options={authUser.map((doctor) => ({ value: authUser.id, label: authUser.name + ' ' + authUser.surname + ' | ' + authUser.email }))}
          />*/}
        </Grid>
      </Grid>
    </InfoSection>
  )
}
